/**
 * Choosing which faces to paint on.
 *
 * A classic selection tool, so it behaves like one: it is a mode you turn on,
 * clicking picks, Escape clears, and nothing about it is remembered. The state
 * lives in `shared/selection.ts` and never reaches the file — see the note there
 * on why this is a tool mode rather than a property of a layer.
 *
 * **The template is the picker.** There is no list of eighteen checkboxes,
 * because the template already shows you the faces and you already know which
 * one you mean by pointing at it. Clicking a face toggles it; holding Alt or
 * Shift toggles the whole strip it belongs to, which is how you get "the right
 * arm" without six clicks.
 *
 * While the mode is on, a click must not paint. That is done by listening in
 * the CAPTURE phase on the canvas and stopping the event there, rather than by
 * teaching the painter about selection modes — the painter's job is strokes,
 * and a tool that quietly swallows input is easier to reason about than a
 * painter that sometimes declines to.
 */

import type { RegionId } from "../../shared/format";
import { CANVAS, REGION_IDS } from "../../shared/format";
import { REGIONS, regionAt } from "../../shared/regions";
import {
  ALL,
  add,
  describe,
  EMPTY,
  excludedFaces,
  type FaceGroup,
  faceLabel,
  facesOf,
  groupOf,
  invert,
  type Selection,
  selectOnly,
  toggleFace,
  toggleGroup,
} from "../../shared/selection";

export interface FaceSelectOptions {
  /** Where the rail button mounts. */
  mount: HTMLElement;
  /** The paint canvas — the picking surface, and the thing clicks are taken from. */
  canvas: HTMLCanvasElement;
  /** Whether the faces tool is the active one. Owned by the rail. */
  active: () => boolean;
  /** Escape leaves the tool as well as clearing — the shell decides what to. */
  onEscape?: () => void;
  /** Fires whenever the selection or the mode changes. */
  onChange?: (selection: Selection, picking: boolean) => void;
  /**
   * One undoable step, at the end of the gesture that made it.
   *
   * Fired per GESTURE rather than per face: a drag across four faces is one
   * step, or undoing a limb selection takes six presses. Not fired at all when
   * the gesture changed nothing, and not fired for `set`, which is what undo
   * itself calls.
   */
  onEdit?: (before: RegionId[], after: RegionId[], label: string) => void;
  doc?: Document;
}

/**
 * How much one tap takes.
 *
 * "part" rather than "limb", and the rename is the point: the coarse mode also
 * takes the torso, which is not a limb, and the editor had two words for this
 * one idea — the picker said Limb while Fill said Whole part, for the same
 * scope. The code underneath has only ever said part (`partFaces`, `partGroup`,
 * `<part>.<face>`), so this is the UI catching up rather than a new term.
 */
export type Granularity = "face" | "part";

export interface FaceSelect {
  /** What a tap takes. See `setGranularity`. */
  granularity(): Granularity;
  /**
   * Choose what a tap takes.
   *
   * A real control rather than a modifier key, and that is the decision that
   * makes this usable on a tablet at all: Alt and Shift do not exist there, and
   * "select the whole right arm" is the common case rather than the advanced
   * one. The modifiers stay as a desktop accelerator for whichever granularity
   * you are NOT currently in.
   */
  setGranularity(next: Granularity): void;
  /** Every face. Not the same value as clearing — see `ALL` in selection.ts. */
  selectAll(): void;
  /** Swap what is selected for what is not. */
  invertSelection(): void;
  /** What may be painted. Read per stroke by the painter. */
  selection(): Selection;
  /**
   * Put a selection back, without recording it.
   *
   * What undo and redo call. Records nothing by design — a history entry that
   * described restoring a history entry is how an undo stack starts undoing
   * itself.
   */
  set(faces: readonly RegionId[]): void;
  /** Whether the picker is taking clicks — i.e. whether its tool is active. */
  picking(): boolean;
  clear(): void;
  /**
   * Toggle one face, as one undo step.
   *
   * The checklist's write path, and the reason it exists as a method rather
   * than the checklist calling `toggleFace` and `set` itself: doing that would
   * bypass `commitStep` and the checklist would edit the selection without
   * recording history, so a box ticked in the panel could not be undone while
   * the same face tapped on the canvas could. Two surfaces, one operation.
   */
  toggle(id: RegionId): void;
  /** Toggle a whole strip, as one undo step. The checklist's parent rows. */
  toggleLimb(group: FaceGroup): void;
  /**
   * Watch the selection.
   *
   * The checklist has to re-tick itself when a face is picked on the canvas, or
   * the two surfaces disagree about what is selected — which is exactly the
   * drift the plan warns against. Returns an unsubscribe.
   */
  subscribe(fn: (selection: Selection) => void): () => void;
  /** Draw the selection onto the overlay, in template coordinates. */
  draw(ctx: CanvasRenderingContext2D, hovered: RegionId | null): void;
  destroy(): void;
}

/** Matches the UI face used everywhere else, with a stack to fall back on. */
const FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';
/** Breathing room inside a label's pill, in texels. */
const PAD_X = 7;
const PAD_Y = 4;

const ICON =
  '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" ' +
  'stroke-linecap="round" stroke-linejoin="round">' +
  // A marquee with one corner lifted: a selection, not a shape tool.
  '<path d="M4 8V5a1 1 0 0 1 1-1h3" stroke-dasharray="0"/>' +
  '<path d="M16 4h3a1 1 0 0 1 1 1v3"/><path d="M20 16v3a1 1 0 0 1-1 1h-3"/>' +
  '<path d="M8 20H5a1 1 0 0 1-1-1v-3"/><path d="M9 12h6"/><path d="M12 9v6"/></svg>';

export function mountFaceSelect(opts: FaceSelectOptions): FaceSelect {
  const doc = opts.doc ?? document;

  /*
    A rail TOOL button, not a mode toggle beside the tools.

    `data-tool` is what `wireTools` binds, so the click, the pressed state and
    the second-click-opens-options behaviour all come from the rail rather than
    from here — and choosing Brush puts this down, which is what a selection
    tool does everywhere else. It must exist before `wireTools` queries for
    `[data-tool]`, which is why this mounts first.
  */
  const btn = doc.createElement("button");
  btn.type = "button";
  btn.className = "icon-btn";
  btn.dataset.tool = "faces";
  btn.setAttribute("aria-pressed", "false");
  btn.setAttribute("aria-label", "Select faces");
  btn.title = "Choose which faces to paint on — tap a face, Alt-tap a part";
  btn.innerHTML = ICON;
  opts.mount.appendChild(btn);

  let selection: Selection = EMPTY;
  let granularity: Granularity = "face";
  let lastLabel = "select face";
  /** Whether this tool is the active one. Owned by the rail, read from it. */
  const picking = () => opts.active();
  /*
    The selection as it stood when the current gesture began, or null between
    gestures.

    A gesture is pointerdown to pointerup. Recording at the END of one is what
    makes a drag across four faces a single undo step — and it is written now,
    before drag-select exists, because retro-fitting a gesture boundary onto a
    per-click recorder means unpicking every entry it already wrote.
  */
  let gestureFrom: RegionId[] | null = null;
  /*
    The face the pointer is currently over, while a drag is under way.

    Held so a drag re-entering the face it is already on does not keep
    re-applying it — harmless with `add`, but it would spam `onChange` and
    redraw the overlay on every pointermove.
  */
  let dragOver: RegionId | null = null;
  let dragging = false;

  /** Everything watching the selection — the checklist, today. */
  const watchers = new Set<(s: Selection) => void>();

  const announce = () => {
    opts.onChange?.(selection, picking());
    for (const fn of watchers) fn(selection);
  };

  /** Close the current gesture, recording it if it changed anything. */
  function endGesture(label: string) {
    if (!gestureFrom) return;
    const before = gestureFrom;
    gestureFrom = null;
    const after = [...selection];
    if (before.length === after.length && before.every((id) => selection.has(id))) return;
    opts.onEdit?.(before, after, label);
  }

  /** A change made outside a gesture — a button, a shortcut — as one step. */
  function commitStep(next: Selection, label: string) {
    const before = [...selection];
    selection = next;
    announce();
    const after = [...selection];
    if (before.length === after.length && before.every((id) => next.has(id))) return;
    opts.onEdit?.(before, after, label);
  }

  /** Pointer to template pixels — the canvas is letterboxed, so a flat ratio. */
  function templateAt(e: PointerEvent): { x: number; y: number } | null {
    const box = opts.canvas.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return null;
    return {
      x: Math.floor(((e.clientX - box.left) / box.width) * CANVAS.width),
      y: Math.floor(((e.clientY - box.top) / box.height) * CANVAS.height),
    };
  }

  /*
    An ordinary listener now, and that is the point of making this a tool.

    It used to run in the capture phase and stop the event dead, so a click
    would pick a face instead of leaving a dab — a listener that had to
    out-race another listener on the same element. As a tool, painting simply
    is not active, so there is nothing to out-race and nothing to swallow.
  */
  const onDown = (e: PointerEvent) => {
    if (!picking()) return;
    const at = templateAt(e);
    if (!at) return;
    const id = regionAt(at.x, at.y);
    /*
      Dead space clears the selection — a revision, and one that had to wait.

      This used to do nothing, on the stated grounds that losing a selection to
      a misplaced click is unrecoverable. It is recoverable now: a selection
      change is a history entry, so a stray click costs one undo, and matching
      the convention every other editor has beats protecting against something
      undo can take back.

      Recorded through `commitStep`, which no-ops when nothing changed — so
      clicking the margin with no selection does not put an empty entry on the
      stack for somebody to undo through later.
    */
    if (!id) {
      commitStep(EMPTY, "deselect");
      return;
    }
    gestureFrom = [...selection];
    /*
      The control decides; the modifier flips it for one tap.

      Held either way round, so somebody in Part mode can still take a single
      face without leaving the mode — which is what makes the mode safe to leave
      switched on.
    */
    const flip = e.altKey || e.shiftKey;
    const wholePart = granularity === "part" ? !flip : flip;
    selection = wholePart ? toggleGroup(selection, groupOf(id)) : toggleFace(selection, id);
    dragging = true;
    dragOver = id;
    announce();
    /*
      Capture, so a drag that leaves the canvas keeps selecting.

      Without it the pointer events stop the moment the finger crosses onto the
      panel below, and a drag down a limb strip ends early with half of it
      picked — which reads as the drag being unreliable rather than as a
      boundary being hit.

      **After the announce, and inside a try**, because it throws NotFoundError
      when the id is not an active pointer — a pointer released before the
      handler ran, or one another element already captured. Thrown from where it
      used to sit, it skipped the announce: the selection changed and nothing on
      screen said so, which is worse than the dropped drag it exists to prevent.
    */
    try {
      opts.canvas.setPointerCapture?.(e.pointerId);
    } catch {
      // No capture. The drag still works; it just ends at the canvas edge.
    }
    // Labelled by what was reached for, so the undo toast reads as an action
    // rather than as a state: "Undid: select part", not "Undid: selection".
    lastLabel = wholePart ? "select part" : "select face";
  };

  /**
   * Extend a drag onto whatever it is passing over.
   *
   * Adds and never removes. A drag that toggled would flicker faces on and off
   * as a finger wobbled back across a boundary it had already crossed, so
   * removing one stays a deliberate tap.
   */
  const onMove = (e: PointerEvent) => {
    if (!dragging || !picking()) return;
    const at = templateAt(e);
    if (!at) return;
    const id = regionAt(at.x, at.y);
    if (!id || id === dragOver) return;
    dragOver = id;
    const flip = e.altKey || e.shiftKey;
    const wholePart = granularity === "part" ? !flip : flip;
    selection = add(selection, wholePart ? facesOf(groupOf(id)) : [id]);
    lastLabel = wholePart ? "select parts" : "select faces";
    announce();
  };

  /*
    The gesture ends on the window, not the canvas.

    A drag that starts on a face and lifts anywhere else still has to close, or
    the next gesture inherits this one's starting point and the two collapse
    into a single undo step covering both.
  */
  const onUp = () => {
    dragging = false;
    dragOver = null;
    endGesture(lastLabel);
  };

  const onKey = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    if (e.key === "Escape" && (selection.size > 0 || picking())) {
      // Escape clears the selection AND leaves the mode, because a selection
      // tool you cannot get out of with Escape is the one everybody complains
      // about.
      commitStep(EMPTY, "deselect");
      opts.onEscape?.();
    }
  };

  opts.canvas.addEventListener("pointerdown", onDown);
  opts.canvas.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  doc.addEventListener("keydown", onKey);

  /**
   * Dim what cannot be painted, outline what can.
   *
   * Dimming the EXCLUDED faces rather than highlighting the selected ones is
   * deliberate: the excluded set is what the selection actually does, and a wash
   * over it reads as "not here" without competing with the artwork underneath in
   * the places you are still working.
   */
  function draw(ctx: CanvasRenderingContext2D, hovered: RegionId | null) {
    const excluded = excludedFaces(selection);
    if (excluded.length > 0) {
      ctx.save();
      ctx.fillStyle = "rgba(12, 16, 22, 0.55)";
      for (const id of excluded) {
        const r = REGIONS[id];
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
      ctx.restore();
    }

    if (selection.size > 0) {
      ctx.save();
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
      for (const id of selection) {
        const r = REGIONS[id];
        ctx.strokeRect(r.x + 0.75, r.y + 0.75, r.w - 1.5, r.h - 1.5);
      }
      ctx.restore();
    }

    // What a click would take, shown before it is taken.
    if (picking() && hovered) {
      const r = REGIONS[hovered];
      ctx.save();
      ctx.fillStyle = "rgba(255, 255, 255, 0.12)";
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.restore();
    }

    if (picking()) drawLabels(ctx, hovered);
  }

  /**
   * Name every face, while the picker is active.
   *
   * The template already prints Roblox's own letters — L, B, R, F — but at 35%
   * under the artwork, and in the naming that is the actual trap: the `L` on the
   * right arm is the side pressed against the torso, not anybody's left. So
   * these are the app's words, `faceLabel`'s, the same ones the chip uses.
   *
   * On every face rather than on the hovered one. A label that only appears
   * under the pointer is hover-only, and principle 4 of
   * interaction-and-devices.md says nothing important may be — which on a tablet
   * means it would not exist at all. The hovered face is emphasised instead.
   *
   * Drawn last, so it sits above the dim wash and the selection outlines rather
   * than under them.
   */
  function drawLabels(ctx: CanvasRenderingContext2D, hovered: RegionId | null) {
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (const id of REGION_IDS) {
      const r = REGIONS[id];
      const strong = id === hovered;
      const text = faceLabel(id).toUpperCase();

      /*
        Sized to the face, not to a constant.

        The template's faces are not one size — a limb strip is 64 wide where
        the chest is 128 — so a label that fits the chest overflows a sleeve.
        Step down until it fits with room to breathe, rather than clipping or
        spilling over the edge into a neighbour.
      */
      let size = strong ? 15 : 13;
      let width = 0;
      while (size > 9) {
        ctx.font = `600 ${size}px ${FONT}`;
        width = ctx.measureText(text).width;
        if (width + PAD_X * 2 <= r.w - 8) break;
        size -= 1;
      }

      /*
        A pill behind the words, because a label has to read on two backgrounds
        at once: an excluded face under a dark wash, and a selected one showing
        whatever has been painted on it. An outline alone survives both and
        looks like text spilled on the artwork; a plate says "this is the
        interface" and guarantees the contrast rather than hoping for it.
      */
      const h = size + PAD_Y * 2;
      const w = width + PAD_X * 2;
      const x = r.x + r.w / 2 - w / 2;
      const y = r.y + r.h / 2 - h / 2;

      ctx.beginPath();
      ctx.roundRect(x, y, w, h, h / 2);
      ctx.fillStyle = strong ? "rgba(12, 16, 22, 0.92)" : "rgba(12, 16, 22, 0.72)";
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = strong ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.16)";
      ctx.stroke();

      ctx.fillStyle = strong ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.82)";
      ctx.fillText(text, r.x + r.w / 2, r.y + r.h / 2 + 0.5);
    }
    ctx.restore();
  }

  return {
    selection: () => selection,
    picking,
    granularity: () => granularity,
    setGranularity(next) {
      if (next === granularity) return;
      granularity = next;
      announce();
    },
    selectAll() {
      commitStep(ALL, "select all faces");
    },
    invertSelection() {
      commitStep(invert(selection), "invert selection");
    },
    toggle(id) {
      commitStep(toggleFace(selection, id), `select ${faceLabel(id).toLowerCase()}`);
    },
    toggleLimb(group) {
      commitStep(toggleGroup(selection, group), `select ${describe(selectOnly(facesOf(group)))}`);
    },
    subscribe(fn) {
      watchers.add(fn);
      fn(selection);
      return () => watchers.delete(fn);
    },
    set(faces) {
      selection = new Set(faces);
      gestureFrom = null;
      announce();
    },
    clear() {
      if (selection.size === 0) return;
      commitStep(EMPTY, "deselect");
    },
    draw,
    destroy() {
      opts.canvas.removeEventListener("pointerdown", onDown);
      opts.canvas.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      doc.removeEventListener("keydown", onKey);
      btn.remove();
    },
  };
}
